/** Search hints only. No permissions, displayed labels or URLs are cached. */
export interface GlobalSearchCandidate {
  id: string;
  name: string;
  contract_number: string | null;
  project_number: number | null;
  workspace_id: string;
}
export interface GlobalSearchTaskCandidate { id: string; title: string }
const MAX_CANDIDATES = 200;
const LIFETIME_MS = 60_000;
let generation = 0;
let cachedTasks: { actorId: string; workspaceId: string; expiresAt: number; candidates: GlobalSearchTaskCandidate[] } | null = null;
let cached: { actorId: string; workspaceId: string; expiresAt: number; candidates: GlobalSearchCandidate[] } | null = null;

export function currentGlobalSearchCandidateGeneration(): number { return generation; }
export function invalidateGlobalSearchCandidates(): number {
  cached = null;
  cachedTasks = null;
  return ++generation;
}
export function storeGlobalSearchCandidates(actorId: string, workspaceId: string, candidates: GlobalSearchCandidate[], expectedGeneration: number): void {
  if (expectedGeneration !== generation || !actorId || !workspaceId) return;
  if (cachedTasks && (cachedTasks.actorId !== actorId || cachedTasks.workspaceId !== workspaceId)) cachedTasks = null;
  // A slower warm-up must not evict candidates learned by a concurrent live search.
  if (cached?.actorId === actorId && cached.workspaceId === workspaceId && cached.expiresAt > Date.now()) {
    rememberGlobalSearchCandidates(actorId, workspaceId, [...cached.candidates, ...candidates], expectedGeneration);
    return;
  }
  cached = { actorId, workspaceId, expiresAt: Date.now() + LIFETIME_MS,
    candidates: candidates.slice(0, MAX_CANDIDATES).map(candidate => ({ ...candidate })) };
}
/** Retain candidates found by live RLS queries; their permissions still are not cached. */
export function rememberGlobalSearchCandidates(actorId: string, workspaceId: string, candidates: GlobalSearchCandidate[], expectedGeneration: number): void {
  if (expectedGeneration !== generation || !actorId || !workspaceId || !candidates.length) return;
  if (cachedTasks && (cachedTasks.actorId !== actorId || cachedTasks.workspaceId !== workspaceId)) cachedTasks = null;
  const previous = cached?.actorId === actorId && cached.workspaceId === workspaceId && cached.expiresAt > Date.now() ? cached : null;
  const merged = new Map<string, GlobalSearchCandidate>();
  for (const candidate of [...candidates, ...(previous?.candidates ?? [])]) {
    if (!merged.has(candidate.id)) merged.set(candidate.id, candidate);
  }
  cached = { actorId, workspaceId, expiresAt: previous?.expiresAt ?? Date.now() + LIFETIME_MS,
    candidates: [...merged.values()].slice(0, MAX_CANDIDATES).map(candidate => ({ ...candidate })) };
}
export function matchesGlobalSearchCandidate(candidate: GlobalSearchCandidate, query: string): boolean {
  const term = query.trim().slice(0, 200).toLowerCase();
  if (!term) return false;
  return candidate.name.toLowerCase().includes(term)
    || !!candidate.contract_number?.toLowerCase().includes(term)
    || (/^\d{1,9}$/.test(term) && candidate.project_number === Number(term));
}
/** IDs must pass fresh scope, record, query and navigation checks before display. */
export function getGlobalSearchCandidateIds(actorId: string, workspaceId: string, query: string): string[] {
  if (!cached || cached.actorId !== actorId || cached.workspaceId !== workspaceId) return [];
  if (cached.expiresAt <= Date.now()) { invalidateGlobalSearchCandidates(); return []; }
  return cached.candidates.filter(candidate => matchesGlobalSearchCandidate(candidate, query))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
    .slice(0, 20).map(candidate => candidate.id);
}

/** Remember only Task results already returned by a successful fresh user query, never a broad warm catalogue. */
export function rememberGlobalSearchTaskCandidates(actorId: string, workspaceId: string, candidates: GlobalSearchTaskCandidate[], expectedGeneration: number): void {
  if (expectedGeneration !== generation || !actorId || !workspaceId || !candidates.length) return;
  if (cached && (cached.actorId !== actorId || cached.workspaceId !== workspaceId)) cached = null;
  const previous = cachedTasks?.actorId === actorId && cachedTasks.workspaceId === workspaceId && cachedTasks.expiresAt > Date.now() ? cachedTasks : null;
  const merged = new Map<string, GlobalSearchTaskCandidate>();
  for (const { id, title } of [...candidates, ...(previous?.candidates || [])]) {
    if (!merged.has(id)) merged.set(id, { id, title });
  }
  cachedTasks = { actorId, workspaceId, expiresAt: previous?.expiresAt ?? Date.now() + LIFETIME_MS,
    candidates: [...merged.values()].slice(0, MAX_CANDIDATES) };
}
/** Matching yields IDs only. Each ID still needs a fresh caller-RLS/nav RPC before display. */
export function getGlobalSearchTaskCandidateIds(actorId: string, workspaceId: string, query: string): string[] {
  if (!cachedTasks || cachedTasks.actorId !== actorId || cachedTasks.workspaceId !== workspaceId) return [];
  if (cachedTasks.expiresAt <= Date.now()) { invalidateGlobalSearchCandidates(); return []; }
  const term = query.trim().slice(0, 200).toLowerCase();
  if (!term) return [];
  return cachedTasks.candidates.filter(candidate => candidate.title.toLowerCase().includes(term))
    .sort((a, b) => a.title.localeCompare(b.title) || a.id.localeCompare(b.id))
    .slice(0, 20).map(candidate => candidate.id);
}
